import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { ChevronDown } from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import { useCategoryDetail } from './context'
import { useCategoryTransactions } from './use-category-transactions'
import { formatAmount, formatShortDate, getTransactionDisplayName } from '@/lib/utils'
import { UNCATEGORIZED_LABEL } from '@/lib/constants'
import { subcategoryShade } from './subcategory-color'

type Props = {
  subcategories: { name: string }[]
}

const TOP_N = 5

export function CategoryTopTransactionsSection({ subcategories }: Props) {
  const { categoryName, categoryColor, filteredTransactions, currentMonth } = useCategoryDetail()
  const categoryTxs = useCategoryTransactions(filteredTransactions, categoryName)
  const [expanded, setExpanded] = useState(false)

  const sorted = useMemo(() => {
    const shadeBySub = new Map<string, number>()
    subcategories.forEach((sub, i) => {
      shadeBySub.set(sub.name, subcategoryShade(i, subcategories.length))
    })
    return [...categoryTxs]
      .map((entry) => {
        const subAmounts = new Map<string, number>()
        for (const item of entry.tx.items) {
          if (item.parent_category_name === categoryName) {
            const sub = item.category_name ?? UNCATEGORIZED_LABEL
            subAmounts.set(sub, (subAmounts.get(sub) ?? 0) + item.amount)
          }
        }
        let dominant = ''
        let dominantAmount = 0
        for (const [sub, amt] of subAmounts) {
          if (amt > dominantAmount) {
            dominant = sub
            dominantAmount = amt
          }
        }
        return {
          ...entry,
          opacity: shadeBySub.get(dominant) ?? 1,
        }
      })
      .sort((a, b) => b.categoryAmount - a.categoryAmount)
  }, [categoryTxs, subcategories, categoryName])

  if (sorted.length === 0) return null

  const visible = expanded ? sorted : sorted.slice(0, TOP_N)
  const hasMore = sorted.length > TOP_N

  return (
    <>
      <Separator />
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-muted-foreground">大きい支出</h3>
        <div className="space-y-0.5">
          {visible.map(({ tx, categoryAmount: amount, opacity }) => (
            <Link
              key={tx.id}
              to={`/transactions/${tx.id}?month=${currentMonth}`}
              className="flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-accent"
            >
              <span className="shrink-0 w-10 text-xs text-muted-foreground tabular-nums">
                {formatShortDate(tx.date)}
              </span>
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: categoryColor, opacity }}
              />
              <span className="flex-1 min-w-0 truncate text-sm">
                {getTransactionDisplayName(tx)}
              </span>
              <span className="shrink-0 text-sm tabular-nums">{formatAmount(amount)}</span>
            </Link>
          ))}
        </div>
        {hasMore && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center justify-center gap-1 w-full py-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            {expanded ? '閉じる' : `もっと見る (残り ${sorted.length - TOP_N}件)`}
            <ChevronDown
              className={`size-3 transition-transform ${expanded ? 'rotate-180' : ''}`}
            />
          </button>
        )}
      </div>
    </>
  )
}
